/**
 * ESTADO DE LA CONEXIÓN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ NO USAMOS `navigator.onLine` A SECAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `navigator.onLine` solo dice si hay una red conectada, **no si hay internet**.
 * El caso clásico de un negocio: el celular está pegado al wifi del local, el
 * wifi está prendido, pero el módem no tiene señal. `navigator.onLine` responde
 * `true` y el sistema cree que puede hablar con el servidor.
 *
 * Así que preguntamos de verdad: un `fetch` a `/health` con un plazo corto. Si no
 * responde, estamos sin conexión — sin importar lo que diga el navegador.
 *
 * Los eventos `online`/`offline` del navegador sí se escuchan, pero solo como
 * DISPARADOR para volver a comprobar, nunca como fuente de verdad.
 */

import { create } from "zustand";

/** Cuánto esperamos a que el servidor responda antes de darlo por caído. */
const PLAZO_MS = 4000;

/** Cada cuánto reintentamos cuando estamos sin conexión. */
const REINTENTO_SIN_CONEXION_MS = 15_000;

/** Cada cuánto confirmamos que seguimos conectados. */
const CONFIRMACION_CON_CONEXION_MS = 60_000;

type EstadoConexion = {
  /** Nuestra verdad: el servidor respondió hace poco. */
  enLinea: boolean;
  comprobando: boolean;
  ultimaComprobacion: number | null;
  /** Momento en que se perdió la conexión, para poder decir "sin conexión desde…". */
  desde: number | null;

  comprobar: () => Promise<boolean>;
  iniciarVigilancia: () => () => void;
};

async function servidorResponde(): Promise<boolean> {
  const abortar = new AbortController();
  const temporizador = setTimeout(() => abortar.abort(), PLAZO_MS);

  try {
    const r = await fetch("/health", {
      method: "GET",
      // `no-store` evita que el navegador (o el service worker) responda con una
      // copia guardada y nos haga creer que hay internet cuando no lo hay.
      cache: "no-store",
      signal: abortar.signal,
    });
    return r.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(temporizador);
  }
}

export const useConexion = create<EstadoConexion>((set, get) => ({
  // Arrancamos optimistas: si de verdad no hay internet, la primera comprobación
  // lo corrige en menos de cuatro segundos. Al revés (arrancar en "sin conexión")
  // haría parpadear un aviso de error a todo el mundo al abrir la app.
  enLinea: true,
  comprobando: false,
  ultimaComprobacion: null,
  desde: null,

  comprobar: async () => {
    if (get().comprobando) return get().enLinea;

    set({ comprobando: true });
    const responde = await servidorResponde();
    const antes = get().enLinea;

    set({
      enLinea: responde,
      comprobando: false,
      ultimaComprobacion: Date.now(),
      desde: responde ? null : (antes ? Date.now() : get().desde),
    });

    return responde;
  },

  /**
   * Vigila la conexión. Devuelve la función para dejar de vigilar.
   *
   * Cuando estamos sin conexión revisamos cada 15 segundos (queremos enterarnos
   * rápido de que volvió, para sincronizar). Cuando estamos conectados, cada
   * minuto basta.
   */
  iniciarVigilancia: () => {
    let temporizador: number | undefined;

    const programar = () => {
      clearTimeout(temporizador);
      const espera = get().enLinea
        ? CONFIRMACION_CON_CONEXION_MS
        : REINTENTO_SIN_CONEXION_MS;

      temporizador = window.setTimeout(async () => {
        await get().comprobar();
        programar();
      }, espera);
    };

    const alCambiarRed = () => {
      void get().comprobar();
      programar();
    };

    // Al volver a la pestaña conviene comprobar: el celular pudo estar dormido
    // media hora y la conexión pudo cambiar en ese tiempo.
    const alVolverAlFrente = () => {
      if (document.visibilityState === "visible") alCambiarRed();
    };

    window.addEventListener("online", alCambiarRed);
    window.addEventListener("offline", alCambiarRed);
    document.addEventListener("visibilitychange", alVolverAlFrente);

    void get().comprobar();
    programar();

    return () => {
      clearTimeout(temporizador);
      window.removeEventListener("online", alCambiarRed);
      window.removeEventListener("offline", alCambiarRed);
      document.removeEventListener("visibilitychange", alVolverAlFrente);
    };
  },
}));
